// ABOUTME: Platform validation rules mirroring backend platform_rules.py.
// Exports the per-platform format map and the format/aspect helpers built on it.

type ContentFormat =
  | 'post'
  | 'reel'
  | 'story'
  | 'thread'
  | 'pin'
  | 'event'
  | 'offer'
  | 'search_ad'
  | 'video'

interface PlatformRules {
  formats: string[]
  media: string[]
  requiresMedia: boolean
  requiresVideo?: boolean
  maxLength: number
}

export const PLATFORM_FORMAT_MAP: Record<string, PlatformRules> = {
  twitter: {
    formats: ['post', 'thread'],
    media: ['text', 'image', 'video', 'gif'],
    requiresMedia: false,
    maxLength: 280,
  },
  instagram: {
    formats: ['post', 'story', 'reel'],
    media: ['image', 'video', 'carousel'],
    requiresMedia: true,
    maxLength: 2200,
  },
  facebook: {
    formats: ['post', 'story', 'reel'],
    media: ['text', 'image', 'video', 'link'],
    requiresMedia: false,
    maxLength: 63206,
  },
  linkedin: {
    formats: ['post'],
    media: ['text', 'image', 'video', 'document'],
    requiresMedia: false,
    maxLength: 3000,
  },
  tiktok: {
    formats: ['reel'],
    media: ['video'],
    requiresMedia: true,
    requiresVideo: true,
    maxLength: 2200,
  },
  pinterest: { formats: ['pin'], media: ['image', 'video'], requiresMedia: true, maxLength: 500 },
  youtube: {
    formats: ['reel', 'video'],
    media: ['video'],
    requiresMedia: true,
    requiresVideo: true,
    maxLength: 5000,
  },
  gmb: {
    formats: ['post', 'event', 'offer'],
    media: ['text', 'image'],
    requiresMedia: false,
    maxLength: 1500,
  },
  // Paid search (#1934). Deliberately absent from the `PLATFORMS` table in
  // lib/platforms.ts: an ad draft renders as an AdFrame in the Content grid but
  // is never a social publishing target, so it must not reach a platform picker.
  google_ads: {
    formats: ['search_ad'],
    media: ['text', 'image'],
    requiresMedia: false,
    maxLength: 90,
  },
}

/** YouTube's two video formats, as the draft editor offers them. */
export const YOUTUBE_FORMAT_OPTIONS: { value: ContentFormat; label: string }[] = [
  { value: 'reel', label: 'YouTube Short' },
  { value: 'video', label: 'YouTube video' },
]

const FORMAT_ASPECT_RATIOS: Record<string, string> = {
  reel: '9:16',
  story: '9:16',
  post: '1:1',
  pin: '2:3',
}

export const PLATFORM_REEL_DIMENSIONS: Record<string, string> = {
  instagram: '1080x1920',
  facebook: '1080x1920',
  tiktok: '1080x1920',
  youtube: '1080x1920',
}

/** Image-compatible formats (excludes video-only formats like reel, and text-only like thread/event/offer). */
const IMAGE_FORMATS = new Set<ContentFormat>(['post', 'story', 'pin'])

export interface ImageFormatOption {
  value: ContentFormat
  label: string
  aspect: string
}

const FORMAT_LABELS: Record<string, string> = {
  post: 'Post',
  story: 'Story',
  pin: 'Pin',
}

/**
 * Derive valid image format options from selected platforms.
 * Returns only formats that are image-compatible AND supported by at least one selected platform.
 * If no platforms are provided, returns all image formats.
 */
export function getImageFormatsForPlatforms(platforms: string[]): ImageFormatOption[] {
  if (platforms.length === 0) {
    return Array.from(IMAGE_FORMATS).map((f) => ({
      value: f,
      label: FORMAT_LABELS[f] ?? f,
      aspect: FORMAT_ASPECT_RATIOS[f] ?? '1:1',
    }))
  }

  const validFormats = new Set<ContentFormat>()
  for (const platform of platforms) {
    const rules = PLATFORM_FORMAT_MAP[platform]
    if (!rules) continue
    // Only include formats that are image-compatible AND the platform supports image media
    if (!rules.media.includes('image')) continue
    for (const fmt of rules.formats) {
      if (IMAGE_FORMATS.has(fmt as ContentFormat)) {
        validFormats.add(fmt as ContentFormat)
      }
    }
  }

  // If intersection is empty (e.g. only video platforms selected), fall back to all image formats
  if (validFormats.size === 0) {
    return Array.from(IMAGE_FORMATS).map((f) => ({
      value: f,
      label: FORMAT_LABELS[f] ?? f,
      aspect: FORMAT_ASPECT_RATIOS[f] ?? '1:1',
    }))
  }

  return Array.from(validFormats).map((f) => ({
    value: f,
    label: FORMAT_LABELS[f] ?? f,
    aspect: FORMAT_ASPECT_RATIOS[f] ?? '1:1',
  }))
}

export function validateContentForPlatforms(
  contentType: string,
  format: string | null,
  mediaUrls: string[],
  platforms: string[]
): { errors: string[]; warnings: string[] } {
  const errors: string[] = []
  const warnings: string[] = []
  const hasMedia = mediaUrls.length > 0

  for (const platform of platforms) {
    const rules = PLATFORM_FORMAT_MAP[platform]
    if (!rules) {
      warnings.push(`Unknown platform: ${platform}`)
      continue
    }

    if (format && !rules.formats.includes(format)) {
      errors.push(`${platform} does not support the "${format}" format`)
    }

    if (rules.requiresMedia && !hasMedia) {
      errors.push(`${platform} requires media — add an image or video`)
    }

    if (rules.requiresVideo && contentType !== 'video') {
      errors.push(`${platform} requires video content`)
    }

    if (!rules.media.includes(contentType)) {
      warnings.push(`${platform} may not fully support "${contentType}" media`)
    }
  }

  return { errors, warnings }
}
